import React from 'react';
import { Toaster as Sonner } from 'sonner';

import {
  DISPLAY,
  RADIUS_SCALE,
  COLOR_SCALE,
  SIZE_SCALE,
  FLEX,
  PADDING_X,
  PADDING_Y,
  SHADOW_SCALE,
  POSITION,
  TRANSITION,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';

interface ToasterProps {
  [key: string]: unknown; // Sonner Toaster accepts various props
}

const Toaster = (props: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className={`toaster ${DISPLAY.group}`}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          success:
            'group-[.toast]:bg-green-50 group-[.toast]:border-green-200 group-[.toast]:text-green-800',
          error:
            'group-[.toast]:bg-red-50 group-[.toast]:border-red-200 group-[.toast]:text-red-800',
        },
      }}
      {...props}
    />
  );
};

// Success feedback component for completed actions
interface SuccessFeedbackProps {
  message: string;
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const SuccessFeedback: React.FC<SuccessFeedbackProps> = ({
  message: _message,
  description: _description,
  duration: _duration = 3000,
  action: _action,
}) => {
  // This would typically use the toast library
  // For now, it's a placeholder for the success feedback pattern
  return null;
};

// Inline success indicator for form fields and actions
interface InlineSuccessProps {
  message?: string;
  showIcon?: boolean;
  className?: string;
}

const InlineSuccess: React.FC<InlineSuccessProps> = ({ message, showIcon = true, className }) => (
  <div
    className={`flex items-center gap-2 ${COLOR_SCALE.success} animate-in slide-in-from-left-2 duration-300 ${className ?? ''}`}
  >
    {showIcon && (
      <div
        className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${RADIUS_SCALE.full} bg-green-500 ${DISPLAY.flex} ${FLEX.flexItemsCenter} justify-center`}
      >
        <svg
          className={`${SIZE_SCALE.w3} ${SIZE_SCALE['3']} ${COLOR_SCALE.surface}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    )}
    {message && <span className="text-sm font-medium">{message}</span>}
  </div>
);

// Success animation overlay for completed actions
interface SuccessOverlayProps {
  isVisible: boolean;
  children: React.ReactNode;
  message?: string;
}

const SuccessOverlay: React.FC<SuccessOverlayProps> = ({
  isVisible,
  children,
  message = 'Success!',
}) => (
  <div className="relative">
    {children}
    {isVisible && (
      <div
        className={`${POSITION.absolute} ${POSITION.inset0} bg-green-500/10 ${DISPLAY.flex} ${FLEX.alignItemsCenter} ${FLEX.justifyCenter} ${RADIUS_SCALE.lg} animate-in fade-in ${TRANSITION.duration300}`}
      >
        <div
          className={`bg-green-500 ${COLOR_SCALE.surface} ${PADDING_X['4']} ${PADDING_Y['2']} ${RADIUS_SCALE.lg} ${SHADOW_SCALE.lg} animate-in zoom-in-95 duration-200`}
        >
          <div className="flex items-center gap-2">
            <svg
              className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">{message}</span>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Checkmark animation for completed states
interface CheckmarkAnimationProps {
  isVisible: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const CheckmarkAnimation: React.FC<CheckmarkAnimationProps> = ({
  isVisible,
  size = 'md',
  className,
}) => {
  const getSizeClass = (size: 'sm' | 'md' | 'lg') => {
    switch (size) {
      case 'sm':
        return SIZE_SCALE['4'];
      case 'md':
        return SIZE_SCALE['6'];
      case 'lg':
        return SIZE_SCALE['8'];
      default:
        return SIZE_SCALE['6'];
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`${DISPLAY.inlineFlex} ${FLEX.flexItemsCenter} justify-center ${getSizeClass(size)} ${RADIUS_SCALE.full} bg-green-500 ${COLOR_SCALE.surface} animate-in zoom-in-95 duration-300 ${className ?? ''}`}
    >
      <svg
        className="w-full h-full"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={3}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M5 13l4 4L19 7"
          className="animate-draw-checkmark"
        />
      </svg>
    </div>
  );
};

// Action feedback component for user interactions
interface ActionFeedbackProps {
  type: 'click' | 'success' | 'error' | 'shake' | 'glow';
  duration?: number;
  children: React.ReactNode;
  onComplete?: () => void;
}

const ActionFeedbackBase: React.FC<ActionFeedbackProps> = ({
  type,
  duration = 300,
  children,
  onComplete,
}) => {
  const [isAnimating, setIsAnimating] = React.useState(false);

  React.useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setIsAnimating(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [type, duration, onComplete]);

  const getFeedbackClass = (type: ActionFeedbackProps['type']) => {
    switch (type) {
      case 'click':
        return 'feedback-click';
      case 'success':
        return 'feedback-success';
      case 'error':
        return 'feedback-shake';
      case 'glow':
        return 'feedback-glow feedback-glow-active';
      default:
        return '';
    }
  };

  return <div className={isAnimating ? getFeedbackClass(type) : ''}>{children}</div>;
};

const ActionFeedback = withMemoryLeakMonitor(ActionFeedbackBase, {
  componentName: 'ActionFeedback',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 10,
  frequencyThreshold: 2,
});

export {
  Toaster,
  SuccessFeedback,
  InlineSuccess,
  SuccessOverlay,
  CheckmarkAnimation,
  ActionFeedback,
};
